/* eslint-disable max-lines-per-function */
import { LocalTime } from '@js-joda/core';
import { useEffect, useMemo } from 'react';
import { useUpdateEffect } from 'react-use';

import { DoctorSimple } from '@dreryk/egabinet-personnel-frontend-api';
import { PractitionerId } from '@dreryk/egabinet-personnel-published-language-api';

import { TimetableSlotSelectionEvent } from '@dreryk/react-calendar';
import { commonTranslationKeys, convertDateToLocalDate, useFormController } from '@dreryk/react-common';
import { Modal, ModalWidth } from '@dreryk/react-components';

import { useHealthcareLocationQuery } from '~legacy.healthcare-provider';

import { personnelTranslationKeys } from '../../translations';
import { useAdjustStartTimeOrEndTime, useRecurrencePeriodSupplier } from '../common/common.hooks';
import { RecurrencePeriod } from '../common/common.types';
import { findDefaultAvailabilityTypeId, TIME_STEP_IN_MINUTES } from '../common/common.utils';
import { useSearchAvailabilityTypes } from '../common/queries/searchAvailabilityTypes';

import { AvailabilityCreationFormBean } from './availabilityCreation.types';
import { AvailabilityCreationPanel } from './AvailabilityCreationPanel';
import { useCreateAvailabilityCommand } from './AvailabilityCreationPanel.hooks';
import { AvailabilityCreationPanelLoader } from './AvailabilityCreationPanel.loader';

type AvailabilityCreationModalProps = {
   readonly selectedSlot: TimetableSlotSelectionEvent<DoctorSimple>;
   readonly onClose: VoidFunction;
   readonly refetch: VoidFunction;
};

const AvailabilityCreationModal = (props: AvailabilityCreationModalProps): JSX.Element => {
   const { onClose, selectedSlot } = props;
   const { data: availabilityTypes } = useSearchAvailabilityTypes();
   const {
      data: healthcareProviderLocations,
      loading: healthcareProviderLocationLoading,
   } = useHealthcareLocationQuery();
   const { executeAsync: createAvailability } = useCreateAvailabilityCommand(
      PractitionerId.of(props.selectedSlot.resource.practitionerId),
      props.refetch,
      onClose
   );
   const defaultAvailabilityTypeId = useMemo(() => findDefaultAvailabilityTypeId(availabilityTypes), [
      availabilityTypes,
   ]);
   const initialValues = useMemo<AvailabilityCreationFormBean>(() => {
      const selectedEndTime = LocalTime.of(selectedSlot.end.getHours(), selectedSlot.end.getMinutes());

      return {
         availabilityDate: convertDateToLocalDate(selectedSlot.start),
         recurrencePeriod: RecurrencePeriod.EVERY_WEEK,
         availabilityTypeId: defaultAvailabilityTypeId,
         startTime: LocalTime.of(selectedSlot.start.getHours(), selectedSlot.start.getMinutes()),
         endTime: LocalTime.MIDNIGHT.equals(selectedEndTime)
            ? selectedEndTime.minusMinutes(TIME_STEP_IN_MINUTES)
            : selectedEndTime,
         slotDurationFixed: 'none',
         slotDurationManual: null,
         slotValidation: null,
      };
   }, [selectedSlot, defaultAvailabilityTypeId]);
   const {
      register,
      handleSubmit,
      getValue,
      setValue,
      watch,
      formState,
      trigger,
   } = useFormController<AvailabilityCreationFormBean>(initialValues);
   const adjustStartTimeOrEndTime = useAdjustStartTimeOrEndTime(getValue, setValue);

   useEffect(() => {
      if (defaultAvailabilityTypeId && !getValue('availabilityTypeId')) {
         setValue('availabilityTypeId', defaultAvailabilityTypeId);
      }
   }, [defaultAvailabilityTypeId, getValue, setValue]);

   useUpdateEffect(() => {
      if (healthcareProviderLocations?.length === 1) {
         setValue('healthcareProviderLocation', healthcareProviderLocations[0]);
      }
   }, [healthcareProviderLocations]);

   return (
      <Modal
         title={personnelTranslationKeys.addWorkingTime}
         confirmationButtonLabel={commonTranslationKeys.save}
         wrapInForm
         onSubmit={handleSubmit(createAvailability)}
         onCancel={onClose}
         onCloseRequest={onClose}
         width={ModalWidth.NORMAL}
         testId={'createNewAvailability'}
      >
         <AvailabilityCreationPanelLoader
            healthcareLocationsLoading={healthcareProviderLocationLoading}
            healthcareLocations={healthcareProviderLocations}
         >
            <AvailabilityCreationPanel
               register={register}
               availabilityTypes={availabilityTypes ?? []}
               recurrencePeriodSupplier={useRecurrencePeriodSupplier(getValue('recurrencePeriod'))}
               adjustStartTimeOrEndTime={adjustStartTimeOrEndTime}
               doctor={props.selectedSlot.resource}
               watch={watch}
               formState={formState}
               trigger={trigger}
               // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
               healthcareLocations={healthcareProviderLocations!}
            />
         </AvailabilityCreationPanelLoader>
      </Modal>
   );
};

export { AvailabilityCreationModal };
